import { registerPlugin } from '@capacitor/core'
import { getNativePlatform } from '@/core/capacitor'

export interface ProgressPhoto {
  id: string
  imageDataUrl: string
  createdAt: string
  poseSnapshotJson?: string
}

interface NativeProgressPhoto {
  localIdentifier: string
  capturedAt: number
  imageBase64: string
  poseSnapshot?: Record<string, unknown>
}

interface ProgressPhotosPlugin {
  loadProgressPhotos(): Promise<{ photos: NativeProgressPhoto[] }>
  deleteProgressPhoto(options: { localIdentifier: string }): Promise<void>
  exportToPhotosLibrary(options: { localIdentifier: string }): Promise<{ exported: boolean }>
  exportAllToPhotosLibrary(): Promise<{ exportedCount: number }>
}

const ProgressPhotos = registerPlugin<ProgressPhotosPlugin>('ProgressPhotos')

function nativeToProgressPhoto(native: NativeProgressPhoto): ProgressPhoto {
  return {
    id: native.localIdentifier,
    imageDataUrl: `data:image/jpeg;base64,${native.imageBase64}`,
    createdAt: new Date(native.capturedAt * 1000).toISOString(),
    poseSnapshotJson: native.poseSnapshot ? JSON.stringify(native.poseSnapshot) : undefined,
  }
}

export async function getProgressPhotos(): Promise<ProgressPhoto[]> {
  if (getNativePlatform() !== 'ios') {
    return []
  }

  try {
    const result = await ProgressPhotos.loadProgressPhotos()
    return result.photos
      .map(nativeToProgressPhoto)
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
  } catch (error) {
    console.error('Failed to load progress photos:', error)
    return []
  }
}

export async function deleteProgressPhoto(photoId: string): Promise<void> {
  if (getNativePlatform() !== 'ios') {
    return
  }

  await ProgressPhotos.deleteProgressPhoto({ localIdentifier: photoId })
}

export async function exportPhotoToLibrary(photoId: string): Promise<boolean> {
  if (getNativePlatform() !== 'ios') {
    return false
  }

  const result = await ProgressPhotos.exportToPhotosLibrary({ localIdentifier: photoId })
  return result.exported
}

export async function exportAllPhotosToLibrary(): Promise<number> {
  if (getNativePlatform() !== 'ios') {
    return 0
  }

  const result = await ProgressPhotos.exportAllToPhotosLibrary()
  return result.exportedCount
}

export function getComparePair(photos: ProgressPhoto[]) {
  if (photos.length < 2) {
    return null
  }

  const chronological = [...photos].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
  )

  return {
    first: chronological[0],
    latest: chronological[chronological.length - 1],
  }
}

export function formatProgressPhotoDate(value: string, options?: Intl.DateTimeFormatOptions) {
  return new Date(value).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    ...options,
  })
}
